import React, { FC, useState, useEffect, ChangeEvent, useRef, useId } from 'react';
import axiosInstance from 'config/axiosInstance';
import { fetchAll } from 'config/fetchAll';
import {
  ChevronDownIcon,
  CheckIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  XMarkIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/solid';
import { DriverInterface } from 'components/Driver/DriverTable';
import user_icon from '../../../assets/icons/user.svg';
import add_driver_icon from '../../../assets/icons/add-driver.svg';
import { useTranslation } from 'react-i18next';
import { useClassifiedErrorMessages } from 'reusable/useSaveError';
import { useModalDialog } from 'reusable/useModalDialog';
import { isImeComposing } from 'reusable/isImeComposing';
import FetchFailed from 'reusable/FetchFailed';
import { useLatestRequest } from 'reusable/useLatestRequest';

interface Client {
  id: number;
  name: string;
  assign_drivers: string[];
}

interface AssignDriverDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const AssignDriverDialog: FC<AssignDriverDialogProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation('client');
  // EV-338（2026-10-14 PM 判断）。読み上げの名前は、名前が空なら「名前のない運転者」（見える文字は EV-314 の「-」）。
  // 以前は空のまま渡し、「 を追加」のように読まれた。
  const spokenName = (name?: string | null) => name || t('ui.unnamed-driver', { ns: 'common' });
  const [clients, setClients] = useState<Client[]>([]);
  const [drivers, setDrivers] = useState<DriverInterface[]>([]);
  // EV-198。候補は配送先の一覧と絞り込みの文字から毎回求める（読み込みの前に欄へフォーカスが
  // 入っても、読み込んだ後の一覧で候補を出す）。空なら全件。
  const [clientFilter, setClientFilter] = useState<string>('');
  const [filteredDrivers, setFilteredDrivers] = useState<DriverInterface[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [clientSearchTerm, setClientSearchTerm] = useState<string>('');
  const [driverSearchTerm, setDriverSearchTerm] = useState<string>('');
  const [isClientDropdownVisible, setIsClientDropdownVisible] = useState(false);
  // ADM-16-AC2。矢印キーで選んでいる候補の位置（-1 は無し）。
  const [activeIndex, setActiveIndex] = useState(-1);
  const [assignedDrivers, setAssignedDrivers] = useState<DriverInterface[]>([]);
  const [assignedDriversIds, setAssignedDriversIds] = useState<number[]>([]);
  // 割り当てに失敗した理由。以前はコンソールに出すだけだった。
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const errorMessages = useClassifiedErrorMessages();

  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const driverSearchRef = useRef<HTMLInputElement>(null);
  const assignedListRef = useRef<HTMLDivElement>(null);
  // 外した運転者の位置。描画の後に、その位置の外すボタン（無ければ運転者の検索）へフォーカスを移す。
  const pendingFocus = useRef<number | null>(null);
  const clientInputId = useId();
  const clientLabelId = useId();
  const listboxId = useId();
  const optionId = (client: Client) => `${listboxId}-option-${client.id}`;

  const filteredClients = clientFilter
    ? clients.filter((client) => client.name.toLowerCase().includes(clientFilter))
    : clients;

  // EV-318。読み込みの失敗（空の一覧と区別する）と、最後の要求の応答だけを反映するための番号。
  const [fetchError, setFetchError] = useState<unknown>(null);
  const beginRequest = useLatestRequest();
  // PR #253 の Codex の P2（4258023771）。前回に開いたときの一覧は読み込みを始めたときに消し（候補に
  // 古い配送先・運転者が出ない）、両方の一覧を読み終えるまで登録のボタンを使えない（失敗・再試行の間に、
  // 古い配送先・運転者を選んで登録できていた）。選択の欄は無効にしない（開いた直後のフォーカスの置き場所。
  // EV-198）。候補が空なので選べない。
  const [ready, setReady] = useState(false);

  const fetchData = async () => {
    const isCurrent = beginRequest();
    setFetchError(null);
    setReady(false);
    setClients([]);
    setDrivers([]);
    setFilteredDrivers([]);
    try {
      // BE-21。page_size=0では全件が返らないため、ページを順に読む。
      const clientList = await fetchAll<any>('/v1/client_deliveries/');
      if (!isCurrent()) return;
      setClients(clientList);

      const drivers = await fetchAll<any>('/v1/drivers/');
      if (!isCurrent()) return;
      setDrivers(drivers);
      setFilteredDrivers(drivers);
      setReady(true);
    } catch (error) {
      if (!isCurrent()) return;
      console.error('Error fetching data:', error);
      setFetchError(error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
    // EV-318。閉じた後に届いた応答は捨てる（開き直した後の一覧を古い応答で上書きしない）。
    return () => {
      beginRequest();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleClientChange = (e: ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value.toLowerCase();
    setClientSearchTerm(searchValue);

    setClientFilter(searchValue);
    setSelectedClientId(null);
    setIsClientDropdownVisible(true);
    setActiveIndex(-1);
  };

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value.toLowerCase();
    setDriverSearchTerm(searchValue);

    if (searchValue) {
      const filtered = drivers.filter((driver) =>
        (driver.name ?? '').toLowerCase().includes(searchValue)
      );
      setFilteredDrivers(filtered);
    } else {
      setFilteredDrivers(drivers);
    }
  };

  const selectClient = (clientId: number, clientName: string) => {
    setSelectedClientId(clientId);
    setClientSearchTerm(clientName);
    setIsClientDropdownVisible(false);
    setActiveIndex(-1);
  };

  /** 候補を開く。配送先を選んであるときは全件を出し、選んだものから始める（無ければ start）。 */
  const openClientList = (start: 'selected' | 'first' | 'last' | 'none') => {
    const list = selectedClientId !== null ? clients : filteredClients;
    if (selectedClientId !== null) setClientFilter('');
    setIsClientDropdownVisible(true);
    const selectedIndex = list.findIndex((client) => client.id === selectedClientId);
    if (list.length === 0) setActiveIndex(-1);
    else if (selectedIndex >= 0 && start !== 'none') setActiveIndex(selectedIndex);
    else if (start === 'first') setActiveIndex(0);
    else if (start === 'last') setActiveIndex(list.length - 1);
    else setActiveIndex(-1);
  };

  /** 候補を閉じる。選ばずに離れたときは入力を消す（外を押したときと同じ）。 */
  const closeClientList = (leaving: boolean) => {
    setIsClientDropdownVisible(false);
    setActiveIndex(-1);
    if (leaving && selectedClientId === null) {
      setClientSearchTerm('');
      setClientFilter('');
    }
  };

  /**
   * ADM-16-AC2。WAI-ARIA の combobox（一覧の候補・自動で選ばない）のキー操作。
   * ↓・↑ で候補を移り（端で折り返す。閉じていれば開く）、Enter で選ぶ（フォームは送信しない）。
   * Escape は候補だけを閉じる（閉じていればダイアログを閉じる）。Tab は候補を閉じて次の欄へ。
   */
  const handleClientKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (isImeComposing(event)) return; // EV-120
    const count = filteredClients.length;
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        event.preventDefault();
        const down = event.key === 'ArrowDown';
        if (!isClientDropdownVisible) {
          openClientList(down ? 'first' : 'last');
          return;
        }
        if (count === 0) return;
        setActiveIndex((index) => {
          if (index < 0) return down ? 0 : count - 1;
          return (index + (down ? 1 : -1) + count) % count;
        });
        return;
      }
      case 'Enter': {
        if (!isClientDropdownVisible) return; // 閉じていれば、ふだんどおりフォームを送信する
        event.preventDefault();
        const client = filteredClients[activeIndex];
        if (client) selectClient(client.id, client.name);
        return;
      }
      case 'Escape': {
        if (!isClientDropdownVisible) return; // ダイアログを閉じる（useModalDialog）
        event.preventDefault();
        closeClientList(false);
        return;
      }
      case 'Tab': {
        if (isClientDropdownVisible) closeClientList(true);
        return;
      }
      default:
    }
  };

  /** EV-195。外したら、次の外すボタン（無ければ前、それも無ければ運転者の検索）へフォーカスを移す。 */
  const removeAssignedDriver = (driver: DriverInterface, index: number) => {
    pendingFocus.current = index;
    handleDriverSelection(driver);
  };

  useEffect(() => {
    const index = pendingFocus.current;
    if (index === null) return;
    pendingFocus.current = null;
    const buttons = Array.from(
      assignedListRef.current?.querySelectorAll<HTMLButtonElement>('[data-remove-driver]') ?? []
    );
    const next = buttons[Math.min(index, buttons.length - 1)];
    (next ?? driverSearchRef.current)?.focus();
  }, [assignedDrivers]);

  const handleDriverSelection = (driver: DriverInterface) => {
    if (assignedDrivers.find((d) => d.id === driver.id)) {
      setAssignedDrivers(assignedDrivers.filter((d) => d.id !== driver.id));
      setAssignedDriversIds(
        assignedDriversIds.filter((id) => id !== driver.id)
      );
    } else {
      setAssignedDrivers([...assignedDrivers, driver]);
      setAssignedDriversIds([...assignedDriversIds, driver.id]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // PR #253 の Codex の P2。一覧を読み終えるまでは登録しない（Enter での送信も止める）。
    if (!ready) return;
    try {
      const selectedClient = clients.find(
        (client) => client.id === selectedClientId
      );

      if (selectedClient) {
        const currentDrivers = selectedClient.assign_drivers || [];
        const updatedDrivers = Array.from(
          new Set([...currentDrivers, ...assignedDriversIds])
        );

        await axiosInstance.patch(
          `/v1/client_deliveries/${selectedClientId}/`,
          {
            assign_drivers: updatedDrivers,
          }
        );

        onClose();
        window.location.reload();
      } else {
        setSaveErrors([t('ui.select-client')]);
      }
    } catch (error) {
      console.error('Error assigning client:', error);
      setSaveErrors(errorMessages(error));
    }
  };

  // EV-198。外を押したときの処理は、いまの状態（選んだ配送先）で判断する。以前は登録した時点の
  // 状態（選んでいない）を見続け、選んだ後でも欄の名前を消していた。
  const closeOnOutsideRef = useRef<(event: MouseEvent) => void>(() => {});
  closeOnOutsideRef.current = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node) &&
      inputRef.current &&
      !inputRef.current.contains(event.target as Node)
    ) {
      closeClientList(true);
    }
  };

  const resetData = () => {
    setSelectedClientId(null);
    setClientSearchTerm('');
    setClientFilter('');
    setActiveIndex(-1);
    setDriverSearchTerm('');
    setAssignedDrivers([]);
    // ADM-13。送信用のIDを消し忘れると、次に開いたときに前回の選択が
    // そのまま送られ、意図しない運転者が割り当てられる。
    setAssignedDriversIds([]);
    // RV-028。前回の失敗の説明も消す。消し忘れると、次に開いたときにも出ていた。
    setSaveErrors([]);
  };

  useEffect(() => {
    const onMouseDown = (event: MouseEvent) => closeOnOutsideRef.current(event);
    document.addEventListener('mousedown', onMouseDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      resetData();
    }
  }, [isOpen]);

  // ADM-16。Escape で閉じる・フォーカスを中に留める・閉じたら開いた操作へ戻す。
  // RV-125。見出しの id は文書の中で一意にする（重なったダイアログが別の見出しで読まれた）。
  const headingId = useId();
  const dialogRef = useModalDialog(isOpen, onClose);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed z-10 inset-0 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>
        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>
        <div
          className="inline-block align-middle bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 w-[768px]"
          role="dialog"
          ref={dialogRef}
          aria-modal="true"
          aria-labelledby={headingId}
          tabIndex={-1}
        >
          <div className="bg-white">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-left sm:mt-0 w-full">
                <div className="flex justify-between items-center mt-4 mx-6">
                  <h3
                    className="text-subtitle-1 font-medium text-neutral/500"
                    id={headingId}
                  >
                    {t('manage_client.assign_driver_title')}
                  </h3>
                  <button type="button"
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    <span className="sr-only">{t('ui.close', { ns: 'common' })}</span>
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
                <hr className="border-neutral/50 mt-4 w-full" />
                {/* EV-318。配送先・運転者の一覧を読めなかったことを、空の一覧と区別して出す。 */}
                {fetchError ? (
                  <FetchFailed error={fetchError} onRetry={fetchData} />
                ) : !ready ? (
                  // PR #253 の照合の P3-3。読み込み中（再試行の間を含む）であることを出す（既存の文言）。
                  <p role="status" className="px-6 pt-4 text-caption text-neutral/75">
                    {t('ui.loading', { ns: 'common' })}
                  </p>
                ) : null}
                <form onSubmit={handleSubmit}>
                  <div className="px-6 pt-6">
                    <label
                      id={clientLabelId}
                      htmlFor={clientInputId}
                      className="block text-caption font-normal text-neutral/100"
                    >
                      {t('manage_client.client')}
                    </label>
                    <div className="relative">
                      {/* ADM-16-AC2。WAI-ARIA の combobox（一覧の候補・自動で選ばない）。 */}
                      <input
                        ref={inputRef}
                        id={clientInputId}
                        type="text"
                        role="combobox"
                        aria-autocomplete="list"
                        aria-expanded={isClientDropdownVisible}
                        aria-controls={listboxId}
                        aria-activedescendant={
                          isClientDropdownVisible && filteredClients[activeIndex]
                            ? optionId(filteredClients[activeIndex])
                            : undefined
                        }
                        autoComplete="off"
                        className="block w-full pl-4 pr-12 h-10 text-caption border border-neutral/50 focus:outline-none focus:ring-neutral/75 focus:border-neutral/75 sm:text-sm rounded-md"
                        placeholder={t('manage_client.select_client')}
                        value={clientSearchTerm}
                        onChange={handleClientChange}
                        onKeyDown={handleClientKeyDown}
                        // ADM-16-AC2。候補はフォーカスでは開かず、押す・入力する・↓ で開く（WAI-ARIA の
                        // combobox の例と同じ）。以前はフォーカスで開いたため、ダイアログを開くと候補が
                        // 開いたままになり、最初の Escape が候補を閉じるだけになっていた。
                        onClick={() => {
                          if (!isClientDropdownVisible) openClientList('selected');
                        }}
                      />
                      <ChevronDownIcon
                        className="w-5 h-5 absolute inset-y-3 right-4 text-neutral/75 pointer-events-none"
                        aria-hidden="true"
                      />

                      {isClientDropdownVisible && (
                        <div
                          ref={dropdownRef}
                          className="absolute z-10 mt-1 w-full bg-white border border-neutral/50 rounded-md shadow-lg max-h-60 overflow-y-auto"
                        >
                          <ul id={listboxId} role="listbox" aria-labelledby={clientLabelId}>
                            {filteredClients.map((client, index) => (
                              // マウスの操作。キーボードでは入力欄の ↓↑・Enter で選ぶ（combobox）。
                              <li
                                key={client.id}
                                id={optionId(client)}
                                role="option"
                                aria-selected={index === activeIndex}
                                data-combobox-option
                                className={`cursor-pointer px-4 py-2 hover:bg-gray-200 flex justify-between items-center ${
                                  index === activeIndex ? 'bg-gray-200' : ''
                                } ${
                                  selectedClientId === client.id
                                    ? 'text-primary/300 font-medium'
                                    : 'text-neutral/100'
                                }`}
                                // フォーカスを入力欄に残す（候補へ移すと combobox の操作が途切れる）。
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => selectClient(client.id, client.name)}
                              >
                                {client.name}
                                {selectedClientId === client.id && (
                                  <CheckIcon className="w-5 h-5 text-primary/300" aria-hidden="true" />
                                )}
                              </li>
                            ))}
                          </ul>
                          {filteredClients.length === 0 && (
                            <p className="px-4 py-2 text-gray-500" role="status">
                              {t('ui.no-results', { ns: 'common' })}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-6 px-6 pt-4 pb-6">
                    <div className="w-full">
                      <div className="relative">
                        <input
                          ref={driverSearchRef}
                          type="text"
                          aria-label={t('manage_client.search_driver')}
                          placeholder={t('manage_client.search_driver')}
                          className="block w-full h-10 pl-4 text-caption border border-neutral/50 focus:outline-none focus:ring-neutral/75 focus:border-neutral/75 sm:text-sm rounded-md"
                          value={driverSearchTerm}
                          onChange={handleSearchChange}
                        />
                        <MagnifyingGlassIcon
                          className="w-4 h-4 absolute inset-y-3 right-4 text-neutral/75 pointer-events-none"
                          aria-hidden="true"
                        />
                      </div>
                      <div className="h-80 mt-4 overflow-y-scroll pr-4">
                        {filteredDrivers.map((driver) => (
                          <div
                            key={driver.id}
                            className="flex items-center justify-between py-2 border-b"
                          >
                            <div className="flex items-center">
                              <img
                                src={driver.profile_picture || user_icon}
                                alt=""
                                className="h-10 w-10 object-cover rounded-full"
                              />
                              <div className="ml-4">
                                {/* EV-314。名前・電話番号の空は「-」（EV-311 の表の空と同じ）。 */}
                                <p className="text-body-2 font-medium text-neutral/300">
                                  {driver.name || '-'}
                                </p>
                                <p className="text-caption text-neutral/75">
                                  {driver.phone || '-'}
                                </p>
                              </div>
                            </div>
                            {driver.client_delivery === null ? (
                              // EV-195。追加の前後で同じ button を使い、押した後もフォーカスを失わない。
                              // 追加済みは aria-disabled（押しても何もしない。外すのは右の一覧から）。
                              assignedDrivers.some((d) => d.id === driver.id) ? (
                                <button
                                  type="button"
                                  className="w-8 h-8 flex items-center justify-center"
                                  aria-label={t('manage_client.driver_added', { name: spokenName(driver.name) })}
                                  aria-disabled="true"
                                  onClick={() => {}}
                                >
                                  <CheckCircleIcon className="w-6 h-6 text-success" aria-hidden="true" />
                                </button>
                              ) : (
                                <button
                                  className="w-8 h-8 bg-primary/50 rounded-md flex items-center justify-center"
                                  aria-label={t('manage_client.add_driver', { name: spokenName(driver.name) })}
                                  onClick={() => handleDriverSelection(driver)}
                                  type="button"
                                >
                                  <PlusIcon className="w-4 h-4 text-primary/300" aria-hidden="true" />
                                </button>
                              )
                            ) : (
                              <span className="text-caption text-neutral/100">
                                {t('manage_client.unavailable')}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                    {assignedDrivers.length > 0 ? (
                      <div className="text-left w-full">
                        {/* EV-006。人数は翻訳の表記で出す（英語の driver を固定で付けていた）。 */}
                        <h6 className="text-subtitle-2 font-medium">
                          {t('ui.total-assignment-count', { count: assignedDrivers.length })}
                        </h6>
                        <div ref={assignedListRef} className="mt-4 flex flex-wrap gap-2 overflow-y-scroll">
                          {assignedDrivers.map((driver, index) => (
                            <div
                              key={driver.id}
                              className="flex items-center gap-2 h-14 px-3 rounded-md border border-neutral/50"
                            >
                              <img
                                src={driver.profile_picture || user_icon}
                                alt=""
                                className="h-10 w-10 object-cover rounded-full"
                              />
                              <span className="text-body-2 text-neutral/300">
                                {driver.name || '-'}
                              </span>
                              {/* EV-195。以前は svg の onClick で、キーボードで押せず名前も無かった。 */}
                              <button
                                type="button"
                                data-remove-driver
                                className="text-primary/300"
                                aria-label={t('manage_client.remove_driver', { name: spokenName(driver.name) })}
                                onClick={() => removeAssignedDriver(driver, index)}
                              >
                                <XMarkIcon className="w-6 h-6" aria-hidden="true" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="w-full h-full text-center flex flex-col gap-3 justify-center">
                        <img
                          src={add_driver_icon}
                          alt=""
                          className="h-14"
                        />
                        <h6 className="text-subtitle-2 font-medium">
                          {t('manage_client.assign_driver_title')}
                        </h6>
                        <p className="text-caption text-neutral/75">
                          {t('manage_client.assign_driver_subtitle_1')}
                          <br /> {t('manage_client.assign_driver_subtitle_2')}
                          <br /> {t('manage_client.assign_driver_subtitle_3')}
                        </p>
                      </div>
                    )}
                  </div>
                  {saveErrors.length > 0 && (
                    <div className="px-4 pb-2 text-error text-body-2" role="alert">
                      {saveErrors.map((message) => (
                        <p key={message}>{message}</p>
                      ))}
                    </div>
                  )}
                  <div className="px-4 py-3 border-t-[1px] grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      className="w-full inline-flex justify-center items-center rounded-md border bg-primary/50 text-subtitle-2 text-primary/300"
                      onClick={onClose}
                    >
                      {t('action.cancel')}
                    </button>
                    <button
                      type="submit"
                      disabled={!ready}
                      className="w-full inline-flex justify-center items-center rounded-md bg-primary/300 text-subtitle-2 text-white h-12 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t('action.assign')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignDriverDialog;
